// src/components/BlogTemplate.js
import React from 'react';
import Header from './Header';
import Footer from './Footer';
import '../styles/Blog.css';

import { Helmet } from "react-helmet";
import * as UsefulButtons from "../components/UsefulButtons";

export default function BlogTemplate({
  slug,
  title,
  date,
  topic,
  subheading,
  seo_description,
  image,
  readingTime,
  content
}) {
  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/blog", label: "Blogs & Insights" },
    { to: "#howItWorks", label: "Next Steps" },
    { to: "/get-in-touch", label: "Support" },
  ];

  return (
    <>
      <Helmet>
        {/* Dynamic Title and Description */}
        <title>{title ? `${title} | DataPulse AI Blog & Insights` : "Blogs & Insights | DataPulse AI"}</title>
        <meta name="title" content={title ? `${title} | DataPulse AI Blog & Insights` : "Blogs & Insights | DataPulse AI"} />
        <meta name="description" content={seo_description || "Explore practical insights on automation, AI, and business growth. Learn how DataPulse AI is helping real businesses scale smarter and work faster."} />
        <link rel="canonical" href={`https://www.mydatapulse.co.uk/blog/${slug}`} />

        {/* Open Graph Meta Tags */}
        <meta property="og:site_name" content="DataPulse AI" />
        <meta property="og:title" content={title || "Blogs & Insights | DataPulse AI"} />
        <meta property="og:description" content={seo_description || "Explore practical insights on automation, AI, and business growth."} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={`https://www.mydatapulse.co.uk${image || "/icons/blog-image-og.jpg"}`} />
        <meta property="og:image:alt" content={`Image for blog post: ${title || "DataPulse AI Insight"}`} />
        <meta property="og:url" content={`https://www.mydatapulse.co.uk/blog/${slug}`} />

        {/* Twitter Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title || "Blogs & Insights | DataPulse AI"} />
        <meta name="twitter:description" content={seo_description || "Explore practical insights on automation, AI, and business growth."} />
        <meta name="twitter:image" content={image || "https://www.mydatapulse.co.uk/icons/blog-image-og.jpg"} />
        <meta name="twitter:image:alt" content={`Image for blog post: ${title || "DataPulse AI Insight"}`} />

        {/* Robots */}
        <meta name="robots" content="index, follow" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": title,
            "description": seo_description,
            "image": image || "https://www.mydatapulse.co.uk/icons/blog-image-og.jpg",
            "url": `https://www.mydatapulse.co.uk/blog/${slug}`,
            "datePublished": date,
            "publisher": {
              "@type": "Organization",
              "name": "DataPulse AI",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.mydatapulse.co.uk/logo-clean-large.png"
              }
            }
          })}
        </script>
      </Helmet>
      <Header links={navLinks} />


      <main>
        <section>
          <div className="blog-container">
            <div className="bg-t-100">
            <div className="max-w-screen-xl px-5 pt-8 mx-auto lg:gap-8 xl:gap-0 lg:px-7 lg:pt-8 lg:grid-cols-12">
              <div className="mr-auto place-self-center lg:col-span-7">
                <h1 className="text-5xl leading-none tracking-tight md:text-5xl xl:text-6xl">
                  {title}
                </h1>
                  <a href={`/blog?topic=${topic}`} className="text-title-purple hover:underline">
                    {topic}
                  </a> | {date} • {readingTime} read | <a href={`/blog`} className="text-title-purple hover:underline">Blogs & Insights</a>
              </div>
            </div>
            </div>

            <div className="max-w-screen-xl px-5 pt-8 mx-auto bg-white lg:py-5 lg:px-7">
              <div class="max-w-none mx-auto">
                <div>
                    <p className="mb-6 font-light text-gray-500 lg:mb-8 md:text-lg lg:text-xl">
                    {subheading}
                    <br />
                    <br />
                    {image && (
                        <div className="w-full lg:col-span-5 flex justify-center items-center">
                        <img
                            src={image}
                            alt={`${title} header`}
                            className="w-tquarter max-w-xs rounded shadow-md"
                        />
                        </div>
                    )}
                    </p>
                </div>
                {content}
                <h2>Ready to Explore AI for Your Company?</h2>
                    <p><a href="/get-in-touch">Book a free strategy session</a> to see how AI can help your team grow!</p>
                <hr />
              </div>
            </div>
          </div>
        </section>
        {/* </div> */}


        {/* <!-- Start block */}
        <div className="flex pb-8 justify-center gap-4">
          <UsefulButtons.ChatOnWhatsAppButton text="Get in touch"/>
          <UsefulButtons.ChatWithTeamPageButton text="📋 Send a message"/>
        </div>
        {/* <!-- End block */}

        {/* <!-- Start block */}
        <section className="bg-gray-50 dark:bg-gray-800">
            <div className="max-w-screen-xl px-4 py-8 mx-auto text-center lg:py-16 lg:px-6">
                <figure className="max-w-screen-md mx-auto">
                    <svg className="h-12 mx-auto mb-3 text-gray-400 dark:text-gray-600" viewBox="0 0 24 27" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14.017 18L14.017 10.609C14.017 4.905 17.748 1.039 23 0L23.995 2.151C21.563 3.068 20 5.789 20 8H24V18H14.017ZM0 18V10.609C0 4.905 3.748 1.038 9 0L9.996 2.151C7.563 3.068 6 5.789 6 8H9.983L9.983 18L0 18Z" fill="currentColor"/>
                    </svg>
                    <blockquote>
                        <p className="text-lg font-medium text-gray-900 md:text-lg dark:text-white">"DataPulse AI streamlined our operations and eliminated so much manual work. We’re saving time, cutting costs, and making faster decisions - it’s been a game changer!"</p>
                    </blockquote>
                    <figcaption className="flex items-center justify-center mt-6 space-x-3">
                        <img className="w-6 h-6 rounded-full" src="https://flowbite.s3.amazonaws.com/blocks/marketing-ui/avatars/michael-gouch.png" alt="profile picture"/>
                        <div className="flex items-center divide-x-2 divide-gray-500 dark:divide-gray-700">
                            <div className="pr-3 font-medium text-gray-900 dark:text-white">Will Fender</div>
                            <div className="pl-3 text-sm font-light text-gray-500 dark:text-gray-400">COO of TheStudySpark</div>
                        </div>
                    </figcaption>
                </figure>
            </div>
          </section>
        {/* <!-- End block  */}

        {/* <!-- Start block */}
        <section id="howItWorks" className="bg-white dark:bg-gray-900">
            <div className="max-w-screen-xl px-4 py-10 mx-auto lg:py-24 lg:px-6 text-center">
                <div className="max-w-screen-md mx-auto mb-8 text-center lg:mb-12">
                    <h2 className="text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">How It Works (3 Steps)</h2>
                    <p className="mb-5 font-light text-gray-500 sm:text-xl dark:text-gray-400">We partner with forward-thinking, growth-driven businesses that recognise the long-term value of innovation.</p>
                </div>
                <div className="space-y-8 lg:grid lg:grid-cols-3 sm:gap-6 xl:gap-10 lg:space-y-0">
                    {/* <!-- Pricing Card */}
                    <div className="flex flex-col group max-w-lg p-6 mx-auto text-center text-gray-900 bg-white border border-purple-700 bg-gray-50 hover:bg-purple-100 rounded-lg shadow dark:border-gray-600 xl:p-8 dark:bg-gray-800 dark:text-white">
                        <h3 className="text-2xl font-semibold group-title-purple" style={{ marginTop: '0.5rem' }}>Discovery Session</h3>
                        <p className="font-light text-gray-500 sm:text-lg dark:text-gray-400">Together we'll dive into your workflows, discuss pain points, and explore opportunities to make the biggest impact - while ensuring we’re a great fit for each other.</p>
                        <div className="flex items-baseline justify-center my-6">
                            <img className="w-50pct flex mt-auto rounded-lg lg:mb-0 lg:flex" src="/assets/images/DiscoverySessionImg.png" alt="Discovery Session Image"/>
                        </div>
                    </div>
                    {/* <!-- Pricing Card */}
                    <div className="flex flex-col group max-w-lg p-6 mx-auto text-center text-gray-900 bg-white border border-purple-700 bg-gray-50 hover:bg-purple-100 rounded-lg shadow dark:border-gray-600 xl:p-8 dark:bg-gray-800 dark:text-white">
                        <h3 className="text-2xl font-semibold group-title-purple" style={{ marginTop: '0.5rem' }}>Action Plan</h3>
                        <p className="font-light text-gray-500 sm:text-lg dark:text-gray-400">We come back with a tailored roadmap - built around your goals - with clear timelines, costings, and implementation steps we’ll refine together.</p>
                        <div className="flex items-baseline justify-center my-6">
                            <img className="w-50pct rounded-lg lg:mb-0 lg:flex" src="/assets/images/ActionPlanImg.png" alt="Action Plan Image"/>
                        </div>
                    </div>
                    {/* <!-- Pricing Card */}
                    <div className="flex flex-col group max-w-lg p-6 mx-auto text-center text-gray-900 bg-white border border-purple-700 bg-gray-50 hover:bg-purple-100 rounded-lg shadow dark:border-gray-600 xl:p-8 dark:bg-gray-800 dark:text-white">
                        <h3 className="text-2xl font-semibold group-title-purple" style={{ marginTop: '0.5rem' }}>Delivery</h3>
                        <p className="font-light text-gray-500 sm:text-lg dark:text-gray-400">We handle the build, testing, and rollout - keeping you in the loop every step of the way to ensure a smooth, low-lift transition that delivers real results.</p>
                        <div className="flex items-baseline justify-center my-6">
                            <img className="w-50pct rounded-lg lg:mb-0 lg:flex" src="/assets/images/DeliveryImg.png" alt="Delivery Image"/>
                        </div>
                    </div>
                </div>
                {/* <!-- <a href="#" className="text-white bg-purple-600 hover:bg-purple-700 focus:ring-4 focus:ring-purple-200 font-medium rounded-lg text-sm px-7 py-3 text-center dark:text-white dark:focus:ring-purple-900">Get started</a> */}
                <br/>
              <div class="">
                  <UsefulButtons.ChatWithTeamPageButton text="🚀 Ready to get started?" />
              </div>
            </div>
          </section>
      {/* <!-- End block */}

    </main>

    <Footer />
    </>
  );
}
